import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import Accordion from '../../ui/Accordion'
import Badge from '../../ui/Badge'
import Text from '../../ui/Text'
import Tooltip from '../../ui/Tooltip'
import PlanViewer from '../../PlanViewer'
import QuestionPhotos from './QuestionPhotos'
import TriggerStates, { TRIGGER_STATE_SHAPE, TRIGGER_WARNING, hasStaleState } from './TriggerStates'
import { formatNumber } from '../../ui/KpiCard/status'
import { DATETIME_FORMAT_LABELS, QUESTION_CONTEXT_LABELS } from './constants'

const DATETIME_CONTEXT = 'datetime'
const DATETIME_FORMAT_KEY = 'datetime_format'

// A datetime question is named by the format it captures, which is finer than its context alone.
const questionTypeLabel = question => {
  if (question.context !== DATETIME_CONTEXT)
    return QUESTION_CONTEXT_LABELS.get(question.context) || question.context

  return DATETIME_FORMAT_LABELS.get(question[DATETIME_FORMAT_KEY])
         || QUESTION_CONTEXT_LABELS.get(DATETIME_CONTEXT)
}

const SECTION_ID = 'section_id'
const SECTION_NAME = 'section_name'
const TRIGGERED_GRADE_KEY = 'triggered_grade'
const TRIGGER_STATES_KEY = 'trigger_states'
const SIGNATURE_URL_KEY = 'signature_url'
const GRADE_LABELS_KEY = 'grade_labels'
const PLAN_BACKGROUND_URL_KEY = 'plan_background_url'
const PLAN_PINS_KEY = 'plan_pins'
const EMPTY_PINS = []
const PLAN_CLASS = 'tw:max-w-xs'

const TABLE_CLASS = 'tw:w-full tw:table-fixed tw:border-collapse tw:text-sm'
const TH_CLASS = 'tw:text-left tw:text-xs tw:font-medium tw:text-gray-500 tw:px-3 tw:py-2'
const TD_CLASS = 'tw:px-3 tw:py-3 tw:border-t tw:border-solid tw:border-gray-200 tw:align-top'
const QUESTION_TEXT_CLASS = 'tw:text-gray-900 tw:break-words'
const VALUE_TEXT_CLASS = 'tw:text-gray-900 tw:break-words'
const ANSWER_CELL_CLASS = 'tw:flex tw:items-center tw:gap-1 tw:min-w-0'
const BADGE_CONTENT_CLASS = 'tw:inline-flex tw:items-center tw:gap-1.5'
const TRIGGER_DOT_CLASS = 'tw:inline-block tw:size-2 tw:rounded-full! tw:bg-orange-700'
const SECTION_TITLE_CLASS = 'tw:flex tw:items-center tw:gap-2 tw:min-w-0'
const SECTION_NAME_CLASS = 'tw:min-w-0'
const SIGNATURE_IMG_CLASS = 'tw:max-w-48 tw:max-h-24 tw:border tw:border-solid '
  + 'tw:border-gray-200 tw:rounded-md!'
const TYPE_COL_CLASS = 'tw:w-30'
const QUESTION_COL_CLASS = 'tw:w-2/5'

const EMPTY_VALUE = '--'
const EMPTY_PHOTOS = []
const EMPTY_GRADE_LABELS = []
const EMPTY_STATES = []

const TRIGGER_DOT = <span aria-label="Déclencheur de l’alerte" className={TRIGGER_DOT_CLASS} role="img" />

const triggerStates = question => question[TRIGGER_STATES_KEY] || EMPTY_STATES

const renderTriggerBadge = (states, children = null) => (
  <Tooltip className="tw:inline-flex tw:shrink-0" content={<TriggerStates states={states} />} focusable>
    <Badge variant="warning">
      <span className={BADGE_CONTENT_CLASS}>
        {hasStaleState(states) ? TRIGGER_WARNING : TRIGGER_DOT}
        {children}
      </span>
    </Badge>
  </Tooltip>
)

const isScoredGrade = question =>
  (question[GRADE_LABELS_KEY] || EMPTY_GRADE_LABELS).length > 0 && Number.isInteger(question.grade)

const renderGrade = question => {
  const labels = question[GRADE_LABELS_KEY]
  const label = labels.find(scaleStep => scaleStep.grade === question.grade)?.label || question.value
  const score = `${question.grade}/${Math.max(...labels.map(scaleStep => scaleStep.grade))}`
  const states = triggerStates(question)
  return (
    <span className={ANSWER_CELL_CLASS}>
      <span className={VALUE_TEXT_CLASS}>{label}</span>
      {states.length > 0 ? renderTriggerBadge(states, score) : <Badge variant="neutral">{score}</Badge>}
    </span>
  )
}

const renderValue = question => {
  const photos = question.photos || EMPTY_PHOTOS
  if (photos.length > 0)
    return <QuestionPhotos photos={photos} />
  if (question[SIGNATURE_URL_KEY])
    return <img alt="Signature" className={SIGNATURE_IMG_CLASS} src={question[SIGNATURE_URL_KEY]} />
  if (question[PLAN_BACKGROUND_URL_KEY]) {
    return (
      <div className={PLAN_CLASS}>
        <PlanViewer
          backgroundUrl={question[PLAN_BACKGROUND_URL_KEY]}
          pins={question[PLAN_PINS_KEY] || EMPTY_PINS}
        />
      </div>
    )
  }
  if ((question.value ?? '') === '')
    return <span className="tw:text-gray-500">{EMPTY_VALUE}</span>
  return <Text className={VALUE_TEXT_CLASS} lines={2}>{question.value}</Text>
}

const renderAnswer = question => {
  if (isScoredGrade(question))
    return renderGrade(question)
  const states = triggerStates(question)
  if (states.length === 0)
    return renderValue(question)
  return (
    <span className={ANSWER_CELL_CLASS}>
      {renderValue(question)}
      {renderTriggerBadge(states)}
    </span>
  )
}

const renderTriggerCount = count => {
  if (!count)
    return null
  return <Badge variant="warning">{`${count} déclencheur${count > 1 ? 's' : ''}`}</Badge>
}

const renderTitle = section => {
  const name = <Text className="tw:block" truncate>{`Section : ${section[SECTION_NAME]}`}</Text>
  const trigger = section[TRIGGERED_GRADE_KEY]
  if (!trigger)
    return name
  return (
    <span className={SECTION_TITLE_CLASS}>
      <span className={SECTION_NAME_CLASS}>{name}</span>
      {renderTriggerBadge(trigger.states, formatNumber(trigger.grade))}
    </span>
  )
}

const IssueResponseSection = ({ section, isOpen, onToggle, triggerCount = 0 }) => {
  const handleToggle = useCallback(() => onToggle(section[SECTION_ID]), [onToggle, section])

  return (
    <Accordion
      headerRight={renderTriggerCount(triggerCount)}
      isOpen={isOpen}
      title={renderTitle(section)}
      onToggle={handleToggle}
    >
      <table className={TABLE_CLASS}>
        <colgroup>
          <col className={TYPE_COL_CLASS} />
          <col className={QUESTION_COL_CLASS} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className={TH_CLASS}>Type</th>
            <th className={TH_CLASS}>Question</th>
            <th className={TH_CLASS}>Réponse</th>
          </tr>
        </thead>
        <tbody>
          {section.questions.map(question => (
            <tr key={question.id}>
              <td className={TD_CLASS}>
                <Badge variant="neutral">
                  {questionTypeLabel(question)}
                </Badge>
              </td>
              <td className={TD_CLASS}>
                <Text className={QUESTION_TEXT_CLASS} lines={2}>
                  {question.title}
                </Text>
              </td>
              <td className={TD_CLASS}>{renderAnswer(question)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Accordion>
  )
}

IssueResponseSection.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  section: PropTypes.shape({
    questions: PropTypes.arrayOf(PropTypes.shape({
      context: PropTypes.string,
      grade: PropTypes.number,
      [GRADE_LABELS_KEY]: PropTypes.arrayOf(PropTypes.shape({
        grade: PropTypes.number,
        label: PropTypes.string
      })),
      id: PropTypes.number.isRequired,
      title: PropTypes.string,
      [TRIGGER_STATES_KEY]: PropTypes.arrayOf(TRIGGER_STATE_SHAPE),
      value: PropTypes.string
    })),
    [SECTION_ID]: PropTypes.number,
    [SECTION_NAME]: PropTypes.string,
    [TRIGGERED_GRADE_KEY]: PropTypes.shape({
      grade: PropTypes.number,
      states: PropTypes.arrayOf(TRIGGER_STATE_SHAPE)
    })
  }).isRequired,
  triggerCount: PropTypes.number
}

IssueResponseSection.defaultProps = {
  triggerCount: 0
}

export default IssueResponseSection
